import React, { useCallback, ReactNode } from 'react';
import { Form, InputNumber } from 'antd';
import { FormInstance } from 'antd';
import { getFixedFloatSum } from '../../utils/InvoiceUtils';
import { DotSeparator } from '../common/FormDots';

interface TaxFormFieldsNewProps {
	form: FormInstance;
	formData: any;
	currency: any;
	onTaxChange?: () => void;
	disabled?: boolean;
	/** When CAD fetch sets tax line items, parent passes keys here so UI updates (form field may not be registered). */
	taxKeysFromParent?: number[];
	getIndicator?: (fieldName: string) => ReactNode;
}

const TaxFormFieldsNew: React.FC<TaxFormFieldsNewProps> = ({
	form,
	formData,
	currency,
	onTaxChange,
	disabled = false,
	taxKeysFromParent,
	getIndicator,
}) => {
	const taxKeysFromForm = Form.useWatch('taxKeys', form) || [];
	const taxKeys = (
		taxKeysFromParent != null && taxKeysFromParent.length > 0 ? taxKeysFromParent : taxKeysFromForm
	) as number[];

	const handleSingleTaxChange = useCallback(
		(val: number | string | null) => {
			const taxCharge = val ? parseFloat(String(val)) : 0;
			form.setFieldsValue({ invoiceTax: taxCharge });
			if (onTaxChange) {
				onTaxChange();
			}
		},
		[form, onTaxChange]
	);

	const handleTaxChange = useCallback(
		(val: number | string | null, k: number) => {
			const taxCharge = val ? parseFloat(String(val)) : 0;
			const currentTaxKeys = form.getFieldValue('taxKeys') || [];

			form.setFieldsValue({ [`tax-${k}-amount`]: taxCharge });

			// Use string accumulator since getFixedFloatSum returns a string
			const invoiceTax = currentTaxKeys.reduce((acc: string, k2: number) => {
				const amount =
					k2 === k ? taxCharge : parseFloat(form.getFieldValue(`tax-${k2}-amount`)) || 0;
				return getFixedFloatSum(acc, amount);
			}, '0');

			form.setFieldsValue({ invoiceTax: invoiceTax });
			if (onTaxChange) {
				onTaxChange();
			}
		},
		[form, onTaxChange]
	);

	if (!taxKeys || taxKeys.length === 0) {
		// No tax line items - show single tax input

		return (
			<div className="my-6 flex flex-row items-center gap-x-4">
				<div className="h5medium whitespace-nowrap">Tax</div>
				<DotSeparator />
				<div className="flex items-center gap-2 whitespace-nowrap pl-4">
					{getIndicator && getIndicator('invoiceTax')}
					<Form.Item name="invoiceTax" noStyle>
						<InputNumber
							size="small"
							step={0.01}
							onChange={handleSingleTaxChange}
							style={{ width: 140 }}
							addonAfter={currency.id}
							disabled={disabled}
						/>
					</Form.Item>
				</div>
			</div>
		);
	}

	const taxLineItems = taxKeys.map((k: number, taxIndex: number) => {
		// Use form value when set (e.g. from CAD fetch: GST, HST, PST); else formData or fallback
		const formType = form.getFieldValue(`tax-${k}-taxType`);
		const taxType =
			formType != null && formType !== ''
				? formType
				: formData[`tax-${k}-taxType`] ||
				  (typeof taxKeys[taxIndex] === 'string' ? taxKeys[taxIndex] : null) ||
				  `Tax ${taxIndex + 1}`;
		return (
			<div key={taxIndex} className="flex flex-row items-baseline justify-between gap-x-4 ">
				<div className="flex flex-row flex-nowrap items-center justify-end gap-x-2 whitespace-pre-line break-words">
					<span className="lineItemDetailText whitespace-pre-line break-words">{taxType}</span>
				</div>
				<div className="flex flex-row flex-nowrap items-center justify-end gap-x-2">
					<Form.Item
						name={`tax-${k}-amount`}
						rules={[{ required: true, message: 'Please add tax amount.' }]}
						style={{ marginBottom: 16, flex: 1, maxWidth: 200, alignSelf: 'flex-end' }}
						noStyle
					>
						<InputNumber
							size="small"
							step={0.01}
							style={{ width: '140px' }}
							addonAfter={currency.id}
							onChange={(e) => handleTaxChange(e, k)}
							disabled={disabled}
							size="small"
						/>
					</Form.Item>
				</div>
			</div>
		);
	});

	return (
		<div className="align-end mb-6 flex flex-col">
			<div className="my-6 flex flex-row items-center gap-x-4">
				<div className="h5medium whitespace-nowrap">Tax</div>
				<DotSeparator />
				<div className="flex items-center gap-2 whitespace-nowrap pl-4">
					{getIndicator && getIndicator('invoiceTax')}
					<Form.Item name="invoiceTax" noStyle>
						<InputNumber
							size="small"
							step={0.01}
							onChange={handleSingleTaxChange}
							style={{ width: 140 }}
							addonAfter={currency.id}
							disabled={true}
						/>
					</Form.Item>
				</div>
			</div>
			{taxLineItems}
		</div>
	);
};

export default TaxFormFieldsNew;
